import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Patient, Prescription, PrescriptionGroup } from "common/models/types";
import Button from "components/atoms/Button";
import Text from "components/atoms/Text";
import Spinner from "components/atoms/Spinner";
import Tabs from "../../molecules/Tabs";

// 분리된 탭 컴포넌트 임포트
import PatientInfoTab from "./PatientInfoTab";
import PrescriptionsTab from "./PrescriptionsTab";
import AnalysisTab from "./AnalysisTab";

interface PatientInfoProps {
  patient: Patient | null;
  onSelect: (patient: Patient | null) => void;
  onReload?: (patientId: number) => void;
  isLoading?: boolean;
  onDelete?: (patientId: number) => void;
  isPrescriptionLoading?: boolean;
  onSavePatient?: (patient: Patient) => void;
  onSavePrescriptionGroup?: (group: PrescriptionGroup) => void;
  onDeletePrescriptionGroup?: (groupId: string, prescriptions: Prescription[]) => void;
}

// 탭 타입 정의
enum TabType {
  INFO = "환자 정보",
  PRESCRIPTIONS = "처방전",
  ANALYSIS = "분석",
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const PatientInfo: React.FC<PatientInfoProps> = ({
  patient,
  onSelect,
  onReload,
  isLoading,
  onDelete,
  isPrescriptionLoading,
  onSavePatient,
  onSavePrescriptionGroup,
  onDeletePrescriptionGroup,
}) => {
  // 현재 활성화된 탭
  const [activeTab, setActiveTab] = useState<TabType>(TabType.INFO);

  // 환자 정보 상태 재설정
  useEffect(() => {
    if (patient) {
      // 환자 정보가 변경될 때 탭을 기본 탭으로 재설정
      setActiveTab(TabType.INFO);
    }
  }, [patient?.id]);

  // 새 환자인지 확인 (ID가 없는 경우)
  const isNewPatient = patient && !patient.id;

  // 환자가 선택되지 않은 경우
  if (!patient) {
    return (
      <Container>
        <EmptyState>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            환자를 선택하거나 새로운 환자를 추가해주세요
          </Text>
        </EmptyState>
      </Container>
    );
  }

  // 로딩 중인 경우
  if (isLoading) {
    return (
      <Container>
        <EmptyState>
          <Spinner size="Large" />
          <Text
            textType="Body1"
            color={COLOR_SYSTEM.get("Gray")?.[500]}
            style={{ marginTop: "16px" }}
          >
            환자 정보를 불러오는 중입니다
          </Text>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Text textType="H4">{patient.id ? `환자 ${patient.id}` : "신규 환자"}</Text>
        <ButtonGroup>
          {patient.id && (
            <Button
              buttonType="button"
              color="Negative"
              size="Small"
              type="Text"
              variant="Outlined"
              onClick={() => onDelete?.(patient.id as number)}
            >
              삭제
            </Button>
          )}
        </ButtonGroup>
      </Header>

      {!isNewPatient && (
        <TabsContainer>
          <Tabs
            tabs={[
              { id: TabType.INFO, label: TabType.INFO },
              { id: TabType.PRESCRIPTIONS, label: TabType.PRESCRIPTIONS },
              { id: TabType.ANALYSIS, label: TabType.ANALYSIS },
            ]}
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab as TabType)}
          />
        </TabsContainer>
      )}

      <ContentContainer>
        {/* 새 환자이거나 INFO 탭이 활성화된 경우 PatientInfoTab 표시 */}
        {(isNewPatient || activeTab === TabType.INFO) && (
          <PatientInfoTab
            patient={patient}
            onSavePatient={onSavePatient}
            initialEditMode={isNewPatient}
          />
        )}

        {/* 기존 환자이고 PRESCRIPTIONS 탭이 활성화된 경우에만 표시 */}
        {!isNewPatient && activeTab === TabType.PRESCRIPTIONS && (
          <PrescriptionsTab
            patient={patient}
            onReloadPatient={onReload}
            isPrescriptionLoading={isPrescriptionLoading}
            onSavePrescriptionGroup={onSavePrescriptionGroup}
            onDeletePrescriptionGroup={onDeletePrescriptionGroup}
          />
        )}

        {/* 기존 환자이고 ANALYSIS 탭이 활성화된 경우에만 표시 */}
        {!isNewPatient && activeTab === TabType.ANALYSIS && (
          <AnalysisTab patient={patient} onReloadPatient={onReload} />
        )}
      </ContentContainer>
    </Container>
  );
};

export default PatientInfo;
